import { useCallback, useMemo } from 'react';
import {
  ProductType,
  Session,
  useAddProductToPlan,
  usePlan,
  useRemoveProductFromPlan,
} from 'queries';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { PriceEditionLine } from './PriceEditionLine';
import { useToast } from '@app/core';
import { PriceEditionCountriesTable } from './PriceEditionCountriesTable';

interface Props {
  sessionLinkId?: string;
  session: Session;
}

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
}));

export const PriceEdition = ({ sessionLinkId, session }: Props) => {
  const classes = useStyles();
  const toast = useToast();
  const { data: plan } = usePlan(session.planId);

  const planId = plan?.id;
  const { mutateAsync: addProduct } = useAddProductToPlan(planId);
  const { mutateAsync: removeProduct } = useRemoveProductFromPlan(planId);

  const products = useMemo(() => {
    return (
      plan?.products.filter(
        (planProduct) =>
          !planProduct.sessionLinkId ||
          planProduct.sessionLinkId === sessionLinkId,
      ) || []
    );
  }, [plan, sessionLinkId]);
  const fixedOthers = products.filter(
    (planProduct) =>
      planProduct.product?.type !== ProductType.PARTICIPANT_FEE &&
      planProduct.product?.type !== ProductType.INCENTIVE &&
      !planProduct?.product?.pricePerParticipant,
  );

  const remove = useCallback(
    async (productId: string) => {
      try {
        await removeProduct({
          sessionLinkId,
          productId,
        });
        toast.saved();
      } catch (e) {
        toast.exception(e);
      }
    },
    [removeProduct, sessionLinkId],
  );

  const add = useCallback(
    async (productId: string) => {
      try {
        await addProduct({
          sessionLinkId,
          productId,
        });
        toast.saved();
      } catch (e) {
        toast.exception(e);
      }
    },
    [addProduct, sessionLinkId],
  );
  const othersPerParticipant = products.filter(
    (planProduct) =>
      planProduct.product?.type !== ProductType.PARTICIPANT_FEE &&
      planProduct.product?.type !== ProductType.INCENTIVE &&
      planProduct?.product?.pricePerParticipant,
  );

  return (
    <div className={classes.container}>
      {plan && (
        <PriceEditionCountriesTable
          session={session}
          plan={plan}
          sessionLinkId={sessionLinkId}
        />
      )}
      {!sessionLinkId && (
        <>
          <PriceEditionLine
            label="Options / pptts"
            sessionLinkId={sessionLinkId}
            productLines={othersPerParticipant}
            productType={ProductType.FEATURE}
            addProduct={add}
            removeProduct={remove}
            pricePerParticipant
          />
          <PriceEditionLine
            label="Options fixes"
            sessionLinkId={sessionLinkId}
            productLines={fixedOthers}
            productType={ProductType.FEATURE}
            addProduct={add}
            removeProduct={remove}
            pricePerParticipant={false}
          />
        </>
      )}
    </div>
  );
};
